"use client";
import React, { useEffect } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import { initBranch, trackEvent } from "@/app/utils/branchio";
import { useCleverTapEvents } from "@/app/hooks/use-clevertap";
import { ThirdPartyData } from "@/app/interface/docvault";
import useMobile from "@/app/hooks/use-mobile";
// import { CSK_ISIN } from "@/app/utils/constants";
import { useTransactionFlow } from "@/context/TransactionContext";
import { sendMetaConversionEvent } from "@/app/utils/metaConversion";
import TickImage from "@/app/assets/images/tick.png";
import Image from "next/image";
import FilledStar from "@/app/assets/icons/filled-star";
import { UEListData } from "@/app/interface/homepage";
import { formatIndianCurrency } from "@/app/utils/helper";
import { IconButton } from "@/stories/icon-button/icon-button";
import ArrowRight from "@/app/assets/icons/arrow-right";
import { ButtonSize, ButtonVariant } from "@/stories/button/button";
import { useRouter } from "next/navigation";
import { X } from "lucide-react";
import { getGTM } from "@/app/utils/gtm";

interface Top3StocksModalProps {
    // onComplete: () => void;
    thirdPartyData: ThirdPartyData | undefined;
    userId?: string | undefined;
    userNotInvested?: boolean;
    isin?: string;
    top3Stocks?: UEListData[];
}

const Top3StocksModal: React.FC<Top3StocksModalProps> = ({
    thirdPartyData,
    userId,
    top3Stocks,
    // userNotInvested,
    // isin,
}) => {
    const { isMobile } = useMobile();
    const router = useRouter();
    const { state } = useTransactionFlow();
    const { cleverTapTrackEvent } = useCleverTapEvents();

    useEffect(() => {
        setTimeout(() => {
            if (!state?.postDigioTxnData?.product || !state?.postDigioTxnData?.amount) return;
            const baseEventId = crypto.randomUUID();
            const productEventId = crypto.randomUUID(); // New unique ID for product event
            const eventNameBase = "US_Payment_Done";
            const product = state?.postDigioTxnData?.product;
            const amount = state?.postDigioTxnData?.amount;
            const email = thirdPartyData?.email;

            // Set analytics
            setAnalyticsEvent("", eventNameBase);

            // Facebook Pixel + Meta Conversion
            (async () => {
                const ReactPixel = (await import("react-facebook-pixel")).default;
                ReactPixel.trackCustom(eventNameBase, {
                    value: amount,
                    currency: "INR",
                    eventID: baseEventId,
                });

                await sendMetaConversionEvent({
                    eventName: eventNameBase,
                    eventData: { userId, value: amount, currency: "INR" },
                    eventId: baseEventId,
                });

                ReactPixel.trackCustom(`${eventNameBase}_${product}`, {
                    value: amount,
                    currency: "INR",
                    eventID: productEventId
                });

                await sendMetaConversionEvent({
                    eventName: `${eventNameBase}_${product}`,
                    eventData: { userId, value: amount, currency: "INR" },
                    eventId: productEventId,
                });
            })();
            // GTM Event
            getGTM().then((gtm) => {
                gtm.dataLayer({
                    dataLayer: {
                        event: `${eventNameBase}_GTM`,
                        email,
                        value: state?.postDigioTxnData?.amount,
                        currency: "INR",
                    },
                });
            });

            getGTM().then((gtm) => {
                gtm.dataLayer({
                    dataLayer: {
                        event: `${eventNameBase}_${product}_GTM`,
                        email,
                        value: state?.postDigioTxnData?.amount,
                        currency: "INR",
                    },
                });
            });

            // Branch Events - Generic US_Payment_Done event (device-agnostic)
            initBranch()
                .then(() => {
                    trackEvent(
                        eventNameBase,
                        thirdPartyData?.email || "",
                        thirdPartyData?.phone || "",
                        userId || "",
                        state?.postDigioTxnData?.amount?.toString() || "",
                        product,
                        "INR",
                    );
                    cleverTapTrackEvent(eventNameBase, {
                        email: thirdPartyData?.email || "",
                        phone: thirdPartyData?.phone || "",
                        userId: userId || "",
                        amount: state?.postDigioTxnData?.amount?.toString() || "",
                        product: product,
                        currency: "INR",
                    });
                    // trackEvent(
                    //     `${eventNameBase}_${product}`,
                    //     thirdPartyData?.email || "",
                    //     thirdPartyData?.phone || "",
                    //     userId || "",
                    //     state?.postDigioTxnData?.amount?.toString() || "",
                    //     "INR",
                    // );
                    cleverTapTrackEvent(`${eventNameBase}_${product}`, {
                        email: thirdPartyData?.email || "",
                        phone: thirdPartyData?.phone || "",
                        userId: userId || "",
                        amount: state?.postDigioTxnData?.amount?.toString() || "",
                        currency: "INR",
                    });
                })
                .catch((error) => {
                    console.error("Error initializing Branch:", error);
                });
        }, 350);
    }, []);

    const handleRedirect = () => {
        setAnalyticsEvent("", "KYC_High_Intent_Explore_Unlisted_Shares");
        cleverTapTrackEvent("KYC_High_Intent_Explore_Unlisted_Shares", {
            email: thirdPartyData?.email || "",
            amount: state?.postDigioTxnData?.amount?.toString() || "",
            currency: "INR",
        });
        
        // initBranch()
        //     .then(() => {
        //         trackEvent(
        //             "KYC_High_Intent_Explore_Unlisted_Shares",
        //             thirdPartyData?.email || "",
        //             state?.postDigioTxnData?.amount?.toString() || "",
        //             "INR",
        //         );
        //     })

        //     .catch((error) => console.error("Error initializing Branch:", error));
        window.location.href = `${process.env.ICM_UE_POST_URL_LISTING}`;
    };

    const handleClose = () => {
        setAnalyticsEvent("", "Post_Payment_Cross");
        window.location.reload();
        cleverTapTrackEvent("Post_Payment_Cross", {});
        // initBranch()
        //     .then(() => trackEvent("Post_Payment_Cross"))
        //     .catch((error) => console.error("Error initializing Branch:", error))
        //     .finally(() => {
        //         window.location.reload();
        //     });
    };

    const handleStockClick = (stock: UEListData) => {
        setAnalyticsEvent("", "Post_Payment_View_Details");
        cleverTapTrackEvent("Post_Payment_View_Details", {
            email: thirdPartyData?.email || "",
            phone: thirdPartyData?.phone || "",
            userId: userId,
            ISIN: stock?.ISIN,
        });
        // initBranch()
        //     .then(() =>
        //         trackEvent(
        //             "Post_Payment_View_Details",
        //             thirdPartyData?.email || "",
        //             thirdPartyData?.phone || "",
        //             userId,
        //             stock?.ISIN,
        //         ),
        //     )
        //     .catch((error) => console.error("Error initializing Branch:", error));
        router.push(
            `/unlisted-shares/${stock?.product}/${stock?.company?.displayName?.toLowerCase()?.split(" ")?.join("-")}`,
        );
    };

    return (
        <Dialog open={true}>
            <DialogContent className="w-[90%] !rounded-3xl p-0 md:min-w-[640px] [&>button:last-child]:hidden">
                <VisuallyHidden.Root>
                    <DialogTitle>Top 3 Stocks</DialogTitle>
                </VisuallyHidden.Root>
                <div className="p-0 px-[19px] pb-4 pt-8 md:px-8 md:pb-8 md:pt-14">
                    <div
                        onClick={handleClose}
                        className="fixed right-[1rem] top-[1.3rem] cursor-pointer md:right-[1.5rem]"
                    >
                        <X size={isMobile ? 25 : 30} />
                    </div>
                    <div className="flex w-full flex-col items-center justify-center">
                        <div className="flex flex-col items-center justify-center gap-3">
                            <Image
                                src={TickImage}
                                height={58}
                                width={58}
                                alt="Top 3 Stocks"
                                className="h-14 w-14"
                            />
                            <h1 className="w-[80%] text-center text-sm font-semibold leading-[130%] tracking-[-0.24px] md:text-3xl">
                                Thank you! your order has been placed successfully!
                            </h1>
                            <div className="flex w-[50%] items-center md:w-[80%]">
                                <div className="h-px flex-grow bg-gray-300"></div>
                                <div className="mx-2 text-gray-400">
                                    <FilledStar size={20} className="text-neutral-300" />
                                </div>
                                <div className="h-px flex-grow bg-gray-300"></div>
                            </div>
                        </div>
                        <p
                            onClick={handleRedirect}
                            className="my-6 cursor-pointer text-lg font-semibold leading-[164%] tracking-[-0.18px] text-brand-primary-500 md:mb-6 md:mt-8"
                        >
                            Explore more opportunities
                        </p>
                    </div>
                    <div className="flex flex-col justify-between gap-s9 md:flex-row">
                        {top3Stocks?.map((stock, index) => {
                            return (
                                <div
                                    key={index}
                                    className="flex flex-1 flex-row items-center justify-center gap-s8 rounded-xl border border-neutral-100 px-s7 pb-s6 pt-s6 md:flex-col md:items-start md:justify-start md:gap-2 md:pb-s6 md:pt-s9"
                                >
                                    <Image
                                        src={stock?.logoUrl ?? ""}
                                        alt={`${stock?.displayName} logo`}
                                        width={57}
                                        height={57}
                                        className="h-[84px] w-[84px] rounded-lg border-neutral-100 object-cover md:mb-2 md:h-[57px] md:w-[57px] md:border"
                                    />
                                    <div className="flex-1">
                                        {stock?.displayName && (
                                            <h3
                                                className="font-semibold leading-[130%] md:text-lg"
                                                title={stock?.displayName}
                                            >
                                                {stock?.displayName.length > 13
                                                    ? stock?.displayName?.slice(0, 13)
                                                    : stock?.displayName}
                                                {stock?.displayName.length > 13 ? "..." : ""}
                                            </h3>
                                        )}
                                        <div className="flex gap-s3 md:mb-s8">
                                            <span className="text-base font-semibold leading-[140%]">
                                                {formatIndianCurrency(stock?.price?.unitPrice ?? 0)}
                                            </span>
                                            <span className="text-sm text-neutral-300">•</span>
                                            <span className="text-sm text-neutral-300">{`${stock.minUnits} ${stock.minUnits > 1 ? "shares" : "share"} min`}</span>
                                        </div>
                                    </div>
                                    <IconButton
                                        icon={<ArrowRight />}
                                        className="border border-neutral-100 hover:shadow-sm"
                                        size={ButtonSize.Small}
                                        onClick={() => handleStockClick(stock)}
                                        variant={ButtonVariant.SecondaryDefault}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default Top3StocksModal;
